import React, { useId, useState } from "react";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import { Badge, Chip, EmptyState, NoticeState, Overline, Select } from "../../../ForBrands/ui/BrandUI";
import InsightList from "../../../ForBrands/ui/InsightList";
import { LegendRow } from "../../../ForBrands/ui/LegendItem";
import ResultPill from "../../../ForBrands/ui/ResultPill";
import GameBars, { gameTone } from "../../../ForBrands/ui/charts/GameBars";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { EMPTY, toNum } from "../../../../utils/perfFormat";
import { STAT_CODES, barsFor, boxLine, fmtStat, gameLegend, seasonNotice } from "./athletePerformanceModel";
import { CardNote, SubHead, cx } from "./perfPieces";

// Game by Game (Jayson's A4, spec B3 e and B4; it takes the place of the
// profile's Current season chart):
// - a season selector (every season on file, newest first, camps tagged;
//   grouped under School, Grassroots, College, Pro, Camp and Other events,
//   in that order, when the seasons span two groups or more, D8; picking
//   one loads that season's games into this card only, as on the mock:
//   the card stays on the season it shows until the new one is in, and
//   says so when it has nothing; the picked season's own notices, data
//   kept from the last refresh or games left out, show here);
// - the season in words with its group (or the camp's label) as a passive
//   badge, the profile's competition lines for that year, and a note for a
//   camp's games or for games the team entered;
// - stat pills (Points, Assists, Rebounds, Steals, Blocks, Minutes, True
//   shooting %) for one bar per game: bm-orange for a team win, grey for a
//   loss, hollow grey for a game with no final score, a camp game hollow
//   (desktop) or bm-orange/40 (phones), a game the athlete missed "DNP";
//   the rolling average as an ink line and the season average dashed;
// - each game's detail (date and event, opponent and result, PTS / AST /
//   REB / STL with the picked stat in orange, the full line, the opponent's
//   RPI when the season has it): a hover card on hover or keyboard focus on
//   desktop, the panel under the bars after a tap on phones;
// - the box scores: a table on desktop (the picked stat's column in
//   orange), a compact list on phones with the full lines behind a toggle;
// - the Season outlook read.
// `view` is athletePerformanceModel.gameLogView(); `seasonKey`, `pending`,
// `notice` and `onSeasonChange` come from useAthletePerformance.

const MINI = [
  ["points", "PTS"],
  ["assists", "AST"],
  ["rebounds", "REB"],
  ["steals", "STL"],
];

// One game's detail, in the hover card and the phone panel alike.
export const GameDetail = ({ row, statKey }) => {
  if (!row) return null;
  const line = row.played ? boxLine(row.stats) : "";
  return (
    <span className="flex flex-col gap-2">
      <span className="bm-body-xs text-bm-muted">{[row.day, row.event].filter(Boolean).join(" · ")}</span>
      <span className="flex items-start justify-between gap-2">
        <span className="min-w-0 break-words bm-label-l text-bm-ink">{row.opponent ? `vs ${row.opponent}` : "Opponent not listed"}</span>
        {row.result && <ResultPill result={row.result} score={row.score} />}
      </span>
      {row.played ? (
        <span className="grid grid-cols-4 gap-1">
          {MINI.map(([key, code]) => (
            <span key={key} className="flex flex-col items-center rounded-[8px] bg-bm-mutedbg px-1 py-[6px]">
              <span className={cx("bm-num-m tabular-nums", key === statKey ? "text-bm-orange" : "text-bm-ink")}>{fmtStat(key, row.stats[key])}</span>
              <span className="bm-label-xs text-bm-muted">{code}</span>
            </span>
          ))}
        </span>
      ) : (
        <span className="bm-body-s text-bm-muted">Did not play</span>
      )}
      {line && <span className="bm-body-xs text-bm-muted">{line}</span>}
      {row.rpi && <span className="bm-body-xs text-bm-muted">{`Opponent RPI ${row.rpi}`}</span>}
      {row.camp && (
        <span className="self-start">
          <Badge tone="passive">{row.campLabel}</Badge>
        </span>
      )}
    </span>
  );
};

const statShort = (key, value) => {
  const text = fmtStat(key, value);
  if (text === EMPTY) return EMPTY;
  return key === "ts_pct" ? `${text} TS` : `${text} ${STAT_CODES[key] || ""}`.trim();
};

const COLUMNS = [
  ["minutes", "Min"],
  ["points", "Pts"],
  ["assists", "Ast"],
  ["rebounds", "Reb"],
  ["steals", "Stl"],
  ["blocks", "Blk"],
  ["turnovers", "TO"],
];

const pair = (stats, made, attempted) => {
  const m = toNum(stats[made]);
  const a = toNum(stats[attempted]);
  return m === null || a === null ? EMPTY : `${Math.round(m)}-${Math.round(a)}`;
};

// Desktop box scores (the mock's game log table): points bold and the
// opponent semibold, as on the mock; the picked stat's column in orange.
const GameTable = ({ rows, statKey, hasRpi, caption }) => {
  const th = "bm-th";
  const num = (key) =>
    cx(
      "bm-td text-right bm-body-s tabular-nums",
      key === "points" ? "font-bold" : key === statKey && "font-semibold",
      key === statKey ? "text-bm-orange" : "text-bm-ink",
    );
  return (
    <div className="overflow-x-auto rounded-[8px] border border-bm-border">
      <table className="w-full min-w-[900px] border-collapse bm-table-compact">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col" className={th}>Date</th>
            <th scope="col" className={th}>Event</th>
            <th scope="col" className={th}>Opponent</th>
            {hasRpi && <th scope="col" className={th}>Opp RPI</th>}
            <th scope="col" className={th}>Result</th>
            {COLUMNS.map(([key, label]) => (
              <th key={key} scope="col" className={cx(th, "!text-right", key === statKey && "!text-bm-orange")}>
                {label}
              </th>
            ))}
            <th scope="col" className={cx(th, "!text-right")}>FG</th>
            <th scope="col" className={cx(th, "!text-right")}>3P</th>
            <th scope="col" className={cx(th, "!text-right")}>FT</th>
            <th scope="col" className={cx(th, "!text-right", statKey === "ts_pct" && "!text-bm-orange")}>TS%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="hover:bg-bm-subtle">
              <td className="bm-td whitespace-nowrap bm-body-s text-bm-ink">{r.day}</td>
              <td className="bm-td max-w-[200px] truncate bm-body-s text-bm-muted" title={r.event || undefined}>
                {r.eventShort || EMPTY}
              </td>
              <td className="bm-td bm-body-s font-semibold text-bm-ink">{r.opponent || EMPTY}</td>
              {hasRpi && <td className="bm-td whitespace-nowrap bm-body-s text-bm-muted">{r.rpi || EMPTY}</td>}
              <td className="bm-td whitespace-nowrap">
                {r.result ? <ResultPill result={r.result} score={r.score} /> : <span className="bm-body-s text-bm-muted">{EMPTY}</span>}
              </td>
              {COLUMNS.map(([key], i) => (
                <td key={key} className={num(key)}>
                  {r.played ? fmtStat(key, r.stats[key]) : i === 0 ? "DNP" : EMPTY}
                </td>
              ))}
              <td className={num("fg")}>{r.played ? pair(r.stats, "field_goals_made", "field_goals_attempted") : EMPTY}</td>
              <td className={num("3p")}>{r.played ? pair(r.stats, "three_pointers_made", "three_pointers_attempted") : EMPTY}</td>
              <td className={num("ft")}>{r.played ? pair(r.stats, "free_throws_made", "free_throws_attempted") : EMPTY}</td>
              <td className={num("ts_pct")}>{r.played ? fmtStat("ts_pct", r.stats.ts_pct) : EMPTY}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Phone box scores: one line per game (date, opponent, result, the picked
// stat), the full lines behind a toggle; no table.
const GameList = ({ rows, statKey }) => {
  const [full, setFull] = useState(false);
  const listId = useId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-3">
        <SubHead>Box scores</SubHead>
        <button
          type="button"
          aria-expanded={full}
          aria-controls={listId}
          onClick={() => setFull((v) => !v)}
          className="bm-label-m text-bm-orange underline-offset-2 hover:underline"
        >
          {full ? "Hide full lines" : "Show full lines"}
        </button>
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
      <ul role="list" id={listId} aria-label="Box scores" className="flex flex-col">
        {rows.map((r, i) => (
          <li key={r.key} className={cx("flex flex-col gap-1 py-[10px]", i > 0 && "border-t border-bm-mutedbg")}>
            <span className="flex items-center gap-2">
              <span className="w-[44px] shrink-0 bm-label-m text-bm-muted">{r.day}</span>
              <span className="min-w-0 flex-1 truncate bm-body-s text-bm-ink">{r.opponent ? `vs ${r.opponent}` : EMPTY}</span>
              {r.result && <ResultPill result={r.result} score={r.score} />}
              <span className="min-w-[56px] shrink-0 whitespace-nowrap text-right bm-label-l tabular-nums text-bm-ink">
                {r.played ? statShort(statKey, r.stats[statKey]) : "DNP"}
              </span>
            </span>
            {full && r.played && boxLine(r.stats) && <span className="pl-[52px] bm-body-xs text-bm-muted">{boxLine(r.stats)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

// The season select: one plain list, or an <optgroup> per group (D8;
// `groups` is athletePerformanceModel.seasonGroups(), null for one group).
const SeasonPicker = ({ options, groups, value, onChange, phone }) => {
  if (!options.length) return null;
  if (options.length === 1) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Overline>Season</Overline>
        <span className="bm-label-l text-bm-ink">{options[0].text}</span>
      </div>
    );
  }
  const current = options.some((o) => o.key === value) ? value : options[0].key;
  const optionOf = (o) => (
    <option key={o.key} value={o.key}>
      {o.text}
    </option>
  );
  const items = groups
    ? groups.map((g) =>
      g.label ? (
        <optgroup key={g.key} label={g.label}>
          {g.options.map(optionOf)}
        </optgroup>
      ) : (
        g.options.map(optionOf)
      ),
    )
    : options.map(optionOf);
  if (phone) {
    // The V2 phone select (CoachTeamPage's season pick): bm-select's
    // chevron and right padding keep a long season label clear of it.
    return (
      <div className="flex flex-col gap-2">
        <Overline>Season</Overline>
        <Select aria-label="Choose a season" value={current} onChange={(e) => onChange?.(e.target.value)} className="bm-input-lg">
          {items}
        </Select>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Overline>Season</Overline>
      <Select
        aria-label="Choose a season"
        value={current}
        onChange={(e) => onChange?.(e.target.value)}
        className="!h-12 !w-auto max-w-full !rounded-full !border-bm-strong !pl-5 !pr-10 bm-body-m !text-bm-ink"
      >
        {items}
      </Select>
    </div>
  );
};

const StatPills = ({ stats, value, onChange, phone }) => {
  const pills = stats.map((s) => (
    <Chip key={s.key} active={s.key === value} aria-pressed={s.key === value} onClick={() => onChange(s.key)}>
      {s.label}
    </Chip>
  ));
  return (
    <div
      role="group"
      aria-label="Stat shown on the timeline"
      className={phone ? "-mx-[18px] flex gap-2 overflow-x-auto px-[18px] bm-no-scrollbar [&>*]:shrink-0" : "flex flex-wrap gap-2"}
    >
      {pills}
    </div>
  );
};

const GameByGameCard = ({ view, voice, seasonKey, pending, notice, onSeasonChange, cardProps = {} }) => {
  const phone = usePhoneTheme();
  const [picked, setPicked] = useState(null);
  const statKey = view.stats.some((s) => s.key === picked) ? picked : view.stats[0]?.key || "points";
  const stat = view.stats.find((s) => s.key === statKey) || { key: statKey, label: statKey };
  const bars = barsFor(view.rows, statKey);
  const average = toNum(view.seasonAvg[statKey]);
  const lastRolling = [...bars].reverse().find((b) => b.rolling !== null)?.rolling ?? null;
  const pendingOption = pending ? view.options.find((o) => o.key === pending.key) : null;
  const noticeText = seasonNotice(notice, view.options);
  const yStep = statKey === "ts_pct" ? 20 : statKey === "minutes" ? 10 : undefined;
  const caption = [
    lastRolling !== null && `Rolling ${view.window}-game avg ${fmtStat(statKey, lastRolling)}`,
    average !== null && `Season avg ${fmtStat(statKey, average)}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const ariaLabel = `${stat.label} by game${view.seasonLabel ? `, ${view.seasonLabel}` : ""}${average !== null ? `, season average ${fmtStat(statKey, average)}` : ""}`;

  return (
    <CollapsibleCard
      title="Game by Game"
      subtitle={phone ? "Pick a season, then a stat. Tap a game for its box score." : "Pick a season, then a stat. Hover a game for its box score."}
      {...cardProps}
    >
      <SeasonPicker options={view.options} groups={view.groups} value={pending ? pending.key : seasonKey} onChange={onSeasonChange} phone={phone} />
      {pending && (
        <p role="status" className="bm-body-s text-bm-muted">
          {`${pending.building ? "Building" : "Loading"} ${pendingOption ? pendingOption.label : "that season"}...`}
        </p>
      )}
      {noticeText && <NoticeState role="status">{noticeText}</NoticeState>}
      {(view.notices || []).map((note) => (
        <NoticeState key={note} role="status">
          {note}
        </NoticeState>
      ))}
      <div className="flex flex-col gap-2">
        <p className="bm-body-m text-bm-ink">
          {view.lede}
          {view.badge && (
            <>
              {" "}
              <Badge tone="passive">{view.badge}</Badge>
            </>
          )}
        </p>
        {view.competitions.length > 0 && (
          // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
          <ul role="list" aria-label="Competitions" className="flex flex-col gap-1">
            {view.competitions.map((line, i) => (
              <li key={`${line}-${i}`} className={cx("break-words text-bm-muted", phone ? "bm-body-s" : "bm-body-m")}>
                {line}
              </li>
            ))}
          </ul>
        )}
        {/* The phone season card's camp note type (bm-body-xs) on phones. */}
        <CardNote>{view.campNote}</CardNote>
        <CardNote>{view.enteredNote}</CardNote>
      </div>
      {view.rows.length > 0 ? (
        <>
          <StatPills stats={view.stats} value={statKey} onChange={setPicked} phone={phone} />
          <LegendRow
            items={gameLegend(voice, statKey, view.window, {
              camp: view.camp,
              campLabel: view.campLabel,
              unscored: bars.some((b) => gameTone(b) === "none"),
              // Win and loss only when a bar wears them (a season the team
              // entered may have no final scores at all).
              scored: bars.some((b) => gameTone(b) === "win" || gameTone(b) === "loss"),
              phone,
            })}
            label="Game by Game legend"
          />
          <GameBars
            games={bars}
            format={(v) => fmtStat(statKey, v)}
            average={average}
            averageLabel={average !== null ? `season avg ${fmtStat(statKey, average)}` : undefined}
            rangeLabel={view.range}
            yStep={yStep}
            yUnit={statKey === "ts_pct" ? "%" : ""}
            ariaLabel={ariaLabel}
            listLabel={`${stat.label} by game`}
            caption={caption}
            renderDetail={(i) => <GameDetail row={view.rows[i]} statKey={statKey} />}
          />
          {phone ? (
            <GameList rows={view.rows} statKey={statKey} />
          ) : (
            <GameTable rows={view.rows} statKey={statKey} hasRpi={view.hasRpi} caption={`Box scores${view.seasonLabel ? `, ${view.seasonLabel}` : ""}`} />
          )}
        </>
      ) : (
        <EmptyState>No games on file for this season yet.</EmptyState>
      )}
      <InsightList insights={view.insights} variant="read" label="Season outlook" />
    </CollapsibleCard>
  );
};

export default GameByGameCard;
